/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { BodyType, ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type {
  AcceptReviewPathParameters,
  AddCommentPathParameters,
  AddDocumentPathParameters,
  ApprovePackagePathParameters,
  DeletePackagePathParameters,
  GetKbDocumentArchiveManifestPathParameters,
  GetKbDocumentContentPathParameters,
  GetKbDocumentSourcePathParameters,
  GetKbPackageValidationStatusPathParameters,
  GetKbProgramArchiveManifestPathParameters,
  GetPackageHistoryParams,
  GetPackageHistoryPathParameters,
  GetPackagePathParameters,
  GetPackageStatsParams,
  GetPackageSuggestedPromptsPathParameters,
  ListDocumentsParams,
  ListDocumentsPathParameters,
  ListPackagesParams,
  RejectPackagePathParameters,
  RemoveDocumentPathParameters,
  RetryKbPackagePromotionPathParameters,
  SearchPackagePathParameters,
  SendKbMessagePathParameters,
  SubmitPackagePathParameters,
  TLAddDocumentPackagesRequest,
  TLAddDocumentResponse,
  TLChatMessageResponse,
  TLCommentRequest,
  TLCreatePackageRequest,
  TLHistoryEntryResponse,
  TLHTTPValidationError,
  TLKbArchiveManifestResponse,
  TLKbDocumentSourceResponse,
  TLKbSearchRequest,
  TLKbSearchResponse,
  TLKbSendMessageRequest,
  TLKbSuggestedPromptsResponse,
  TLKbValidationStatusResponse,
  TLPackageDetailResponse,
  TLPackageResponse,
  TLPackageStatsResponse,
  TLPaginatedResponseDocumentResponse,
  TLPaginatedResponseHistoryEntryResponse,
  TLPaginatedResponsePackageResponse,
  TLQueuedMessageResponse,
  TLRejectPackageRequest,
  TLSchemaSyncRequest,
  TLSchemaSyncResultResponse,
  TLSchemaSyncTriggerResponse,
  TLUpdatePackageRequest,
  UpdatePackagePathParameters,
} from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary Get Kb Document Archive Manifest
 */
export const getKbDocumentArchiveManifest = (
  { documentId }: GetKbDocumentArchiveManifestPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLKbArchiveManifestResponse>(
    { url: `/kb/documents/${documentId}/archive-manifest`, method: 'GET', signal },
    options,
  );
};

export const getGetKbDocumentArchiveManifestQueryKey = ({ documentId }: GetKbDocumentArchiveManifestPathParameters) => {
  return ['kb', 'documents', documentId, 'archive-manifest'] as const;
};

export const getGetKbDocumentArchiveManifestQueryOptions = <
  TData = Awaited<ReturnType<typeof getKbDocumentArchiveManifest>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { documentId }: GetKbDocumentArchiveManifestPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getKbDocumentArchiveManifest>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetKbDocumentArchiveManifestQueryKey({ documentId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getKbDocumentArchiveManifest>>> = ({ signal }) =>
    getKbDocumentArchiveManifest({ documentId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: documentId !== null && documentId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getKbDocumentArchiveManifest>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetKbDocumentArchiveManifestQueryResult = NonNullable<
  Awaited<ReturnType<typeof getKbDocumentArchiveManifest>>
>;
export type GetKbDocumentArchiveManifestQueryError = ErrorType<TLHTTPValidationError>;

export function useGetKbDocumentArchiveManifest<
  TData = Awaited<ReturnType<typeof getKbDocumentArchiveManifest>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetKbDocumentArchiveManifestPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getKbDocumentArchiveManifest>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getKbDocumentArchiveManifest>>,
          TError,
          Awaited<ReturnType<typeof getKbDocumentArchiveManifest>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetKbDocumentArchiveManifest<
  TData = Awaited<ReturnType<typeof getKbDocumentArchiveManifest>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetKbDocumentArchiveManifestPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getKbDocumentArchiveManifest>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getKbDocumentArchiveManifest>>,
          TError,
          Awaited<ReturnType<typeof getKbDocumentArchiveManifest>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetKbDocumentArchiveManifest<
  TData = Awaited<ReturnType<typeof getKbDocumentArchiveManifest>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetKbDocumentArchiveManifestPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getKbDocumentArchiveManifest>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Kb Document Archive Manifest
 */

export function useGetKbDocumentArchiveManifest<
  TData = Awaited<ReturnType<typeof getKbDocumentArchiveManifest>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { documentId }: GetKbDocumentArchiveManifestPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getKbDocumentArchiveManifest>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetKbDocumentArchiveManifestQueryOptions({ documentId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Kb Document Archive Manifest
 */
export const prefetchGetKbDocumentArchiveManifestQuery = async <
  TData = Awaited<ReturnType<typeof getKbDocumentArchiveManifest>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { documentId }: GetKbDocumentArchiveManifestPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getKbDocumentArchiveManifest>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetKbDocumentArchiveManifestQueryOptions({ documentId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Kb Document Archive Manifest
 */
export const invalidateGetKbDocumentArchiveManifest = async (
  queryClient: QueryClient,
  { documentId }: GetKbDocumentArchiveManifestPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetKbDocumentArchiveManifestQueryKey({ documentId }) }, options);

  return queryClient;
};

/**
 * @summary Get Kb Document Content
 */
export const getKbDocumentContent = (
  { documentId }: GetKbDocumentContentPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<unknown>({ url: `/kb/documents/${documentId}/content`, method: 'GET', signal }, options);
};

export const getGetKbDocumentContentQueryKey = ({ documentId }: GetKbDocumentContentPathParameters) => {
  return ['kb', 'documents', documentId, 'content'] as const;
};

export const getGetKbDocumentContentQueryOptions = <
  TData = Awaited<ReturnType<typeof getKbDocumentContent>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { documentId }: GetKbDocumentContentPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getKbDocumentContent>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetKbDocumentContentQueryKey({ documentId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getKbDocumentContent>>> = ({ signal }) =>
    getKbDocumentContent({ documentId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: documentId !== null && documentId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getKbDocumentContent>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetKbDocumentContentQueryResult = NonNullable<Awaited<ReturnType<typeof getKbDocumentContent>>>;
export type GetKbDocumentContentQueryError = ErrorType<TLHTTPValidationError>;

export function useGetKbDocumentContent<
  TData = Awaited<ReturnType<typeof getKbDocumentContent>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetKbDocumentContentPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getKbDocumentContent>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getKbDocumentContent>>,
          TError,
          Awaited<ReturnType<typeof getKbDocumentContent>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetKbDocumentContent<
  TData = Awaited<ReturnType<typeof getKbDocumentContent>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetKbDocumentContentPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getKbDocumentContent>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getKbDocumentContent>>,
          TError,
          Awaited<ReturnType<typeof getKbDocumentContent>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetKbDocumentContent<
  TData = Awaited<ReturnType<typeof getKbDocumentContent>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetKbDocumentContentPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getKbDocumentContent>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Kb Document Content
 */

export function useGetKbDocumentContent<
  TData = Awaited<ReturnType<typeof getKbDocumentContent>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { documentId }: GetKbDocumentContentPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getKbDocumentContent>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetKbDocumentContentQueryOptions({ documentId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Kb Document Content
 */
export const prefetchGetKbDocumentContentQuery = async <
  TData = Awaited<ReturnType<typeof getKbDocumentContent>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { documentId }: GetKbDocumentContentPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getKbDocumentContent>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetKbDocumentContentQueryOptions({ documentId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Kb Document Content
 */
export const invalidateGetKbDocumentContent = async (
  queryClient: QueryClient,
  { documentId }: GetKbDocumentContentPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetKbDocumentContentQueryKey({ documentId }) }, options);

  return queryClient;
};

/**
 * @summary Get Kb Document Source
 */
export const getKbDocumentSource = (
  { documentId }: GetKbDocumentSourcePathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLKbDocumentSourceResponse>(
    { url: `/kb/documents/${documentId}/source`, method: 'GET', signal },
    options,
  );
};

export const getGetKbDocumentSourceQueryKey = ({ documentId }: GetKbDocumentSourcePathParameters) => {
  return ['kb', 'documents', documentId, 'source'] as const;
};

export const getGetKbDocumentSourceQueryOptions = <
  TData = Awaited<ReturnType<typeof getKbDocumentSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { documentId }: GetKbDocumentSourcePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getKbDocumentSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetKbDocumentSourceQueryKey({ documentId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getKbDocumentSource>>> = ({ signal }) =>
    getKbDocumentSource({ documentId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: documentId !== null && documentId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getKbDocumentSource>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetKbDocumentSourceQueryResult = NonNullable<Awaited<ReturnType<typeof getKbDocumentSource>>>;
export type GetKbDocumentSourceQueryError = ErrorType<TLHTTPValidationError>;

export function useGetKbDocumentSource<
  TData = Awaited<ReturnType<typeof getKbDocumentSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetKbDocumentSourcePathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getKbDocumentSource>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getKbDocumentSource>>,
          TError,
          Awaited<ReturnType<typeof getKbDocumentSource>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetKbDocumentSource<
  TData = Awaited<ReturnType<typeof getKbDocumentSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetKbDocumentSourcePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getKbDocumentSource>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getKbDocumentSource>>,
          TError,
          Awaited<ReturnType<typeof getKbDocumentSource>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetKbDocumentSource<
  TData = Awaited<ReturnType<typeof getKbDocumentSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetKbDocumentSourcePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getKbDocumentSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Kb Document Source
 */

export function useGetKbDocumentSource<
  TData = Awaited<ReturnType<typeof getKbDocumentSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { documentId }: GetKbDocumentSourcePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getKbDocumentSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetKbDocumentSourceQueryOptions({ documentId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Kb Document Source
 */
export const prefetchGetKbDocumentSourceQuery = async <
  TData = Awaited<ReturnType<typeof getKbDocumentSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { documentId }: GetKbDocumentSourcePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getKbDocumentSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetKbDocumentSourceQueryOptions({ documentId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Kb Document Source
 */
export const invalidateGetKbDocumentSource = async (
  queryClient: QueryClient,
  { documentId }: GetKbDocumentSourcePathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetKbDocumentSourceQueryKey({ documentId }) }, options);

  return queryClient;
};

/**
 * @summary List Packages
 */
export const listPackages = (
  params?: ListPackagesParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLPaginatedResponsePackageResponse>(
    { url: `/kb/packages`, method: 'GET', params, signal },
    options,
  );
};

export const getListPackagesQueryKey = (params?: ListPackagesParams) => {
  return ['kb', 'packages', ...(params ? [params] : [])] as const;
};

export const getListPackagesQueryOptions = <
  TData = Awaited<ReturnType<typeof listPackages>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListPackagesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listPackages>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListPackagesQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listPackages>>> = ({ signal }) =>
    listPackages(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listPackages>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListPackagesQueryResult = NonNullable<Awaited<ReturnType<typeof listPackages>>>;
export type ListPackagesQueryError = ErrorType<TLHTTPValidationError>;

export function useListPackages<
  TData = Awaited<ReturnType<typeof listPackages>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: undefined | ListPackagesParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof listPackages>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listPackages>>,
          TError,
          Awaited<ReturnType<typeof listPackages>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListPackages<
  TData = Awaited<ReturnType<typeof listPackages>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListPackagesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listPackages>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listPackages>>,
          TError,
          Awaited<ReturnType<typeof listPackages>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListPackages<
  TData = Awaited<ReturnType<typeof listPackages>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListPackagesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listPackages>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List Packages
 */

export function useListPackages<
  TData = Awaited<ReturnType<typeof listPackages>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListPackagesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listPackages>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListPackagesQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary List Packages
 */
export const prefetchListPackagesQuery = async <
  TData = Awaited<ReturnType<typeof listPackages>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params?: ListPackagesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listPackages>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getListPackagesQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary List Packages
 */
export const invalidateListPackages = async (
  queryClient: QueryClient,
  params?: ListPackagesParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getListPackagesQueryKey(params) }, options);

  return queryClient;
};

/**
 * @summary Create Package
 */
export const createPackage = (
  tLCreatePackageRequest: BodyType<TLCreatePackageRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLPackageResponse>(
    {
      url: `/kb/packages`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLCreatePackageRequest,
      signal,
    },
    options,
  );
};

export const getCreatePackageQueryKey = (tLCreatePackageRequest?: BodyType<TLCreatePackageRequest>) => {
  return ['POST', 'kb', 'packages', tLCreatePackageRequest] as const;
};

export const getCreatePackageQueryOptions = <
  TData = Awaited<ReturnType<typeof createPackage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLCreatePackageRequest: BodyType<TLCreatePackageRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createPackage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getCreatePackageQueryKey(tLCreatePackageRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof createPackage>>> = ({ signal }) =>
    createPackage(tLCreatePackageRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof createPackage>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type CreatePackageQueryResult = NonNullable<Awaited<ReturnType<typeof createPackage>>>;
export type CreatePackageQueryError = ErrorType<TLHTTPValidationError>;

export function useCreatePackage<
  TData = Awaited<ReturnType<typeof createPackage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLCreatePackageRequest: BodyType<TLCreatePackageRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof createPackage>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof createPackage>>,
          TError,
          Awaited<ReturnType<typeof createPackage>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreatePackage<
  TData = Awaited<ReturnType<typeof createPackage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLCreatePackageRequest: BodyType<TLCreatePackageRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createPackage>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof createPackage>>,
          TError,
          Awaited<ReturnType<typeof createPackage>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreatePackage<
  TData = Awaited<ReturnType<typeof createPackage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLCreatePackageRequest: BodyType<TLCreatePackageRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createPackage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Create Package
 */

export function useCreatePackage<
  TData = Awaited<ReturnType<typeof createPackage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLCreatePackageRequest: BodyType<TLCreatePackageRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createPackage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getCreatePackageQueryOptions(tLCreatePackageRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Create Package
 */
export const prefetchCreatePackageQuery = async <
  TData = Awaited<ReturnType<typeof createPackage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLCreatePackageRequest: BodyType<TLCreatePackageRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createPackage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getCreatePackageQueryOptions(tLCreatePackageRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Create Package
 */
export const invalidateCreatePackage = async (
  queryClient: QueryClient,
  tLCreatePackageRequest: BodyType<TLCreatePackageRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getCreatePackageQueryKey(tLCreatePackageRequest) }, options);

  return queryClient;
};

/**
 * @summary Get Package Stats
 */
export const getPackageStats = (
  params?: GetPackageStatsParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLPackageStatsResponse>({ url: `/kb/packages/stats`, method: 'GET', params, signal }, options);
};

export const getGetPackageStatsQueryKey = (params?: GetPackageStatsParams) => {
  return ['kb', 'packages', 'stats', ...(params ? [params] : [])] as const;
};

export const getGetPackageStatsQueryOptions = <
  TData = Awaited<ReturnType<typeof getPackageStats>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetPackageStatsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPackageStats>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetPackageStatsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getPackageStats>>> = ({ signal }) =>
    getPackageStats(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getPackageStats>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetPackageStatsQueryResult = NonNullable<Awaited<ReturnType<typeof getPackageStats>>>;
export type GetPackageStatsQueryError = ErrorType<TLHTTPValidationError>;

export function useGetPackageStats<
  TData = Awaited<ReturnType<typeof getPackageStats>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: undefined | GetPackageStatsParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPackageStats>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getPackageStats>>,
          TError,
          Awaited<ReturnType<typeof getPackageStats>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetPackageStats<
  TData = Awaited<ReturnType<typeof getPackageStats>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetPackageStatsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPackageStats>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getPackageStats>>,
          TError,
          Awaited<ReturnType<typeof getPackageStats>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetPackageStats<
  TData = Awaited<ReturnType<typeof getPackageStats>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetPackageStatsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPackageStats>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Package Stats
 */

export function useGetPackageStats<
  TData = Awaited<ReturnType<typeof getPackageStats>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetPackageStatsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPackageStats>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetPackageStatsQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Package Stats
 */
export const prefetchGetPackageStatsQuery = async <
  TData = Awaited<ReturnType<typeof getPackageStats>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params?: GetPackageStatsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPackageStats>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetPackageStatsQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Package Stats
 */
export const invalidateGetPackageStats = async (
  queryClient: QueryClient,
  params?: GetPackageStatsParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetPackageStatsQueryKey(params) }, options);

  return queryClient;
};

/**
 * @summary Delete Package
 */
export const deletePackage = (
  { packageId }: DeletePackagePathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<void>({ url: `/kb/packages/${packageId}`, method: 'DELETE', signal }, options);
};

export const getDeletePackageQueryKey = ({ packageId }: DeletePackagePathParameters) => {
  return ['DELETE', 'kb', 'packages', packageId] as const;
};

export const getDeletePackageQueryOptions = <
  TData = Awaited<ReturnType<typeof deletePackage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { packageId }: DeletePackagePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deletePackage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getDeletePackageQueryKey({ packageId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof deletePackage>>> = ({ signal }) =>
    deletePackage({ packageId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: packageId !== null && packageId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof deletePackage>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type DeletePackageQueryResult = NonNullable<Awaited<ReturnType<typeof deletePackage>>>;
export type DeletePackageQueryError = ErrorType<TLHTTPValidationError>;

export function useDeletePackage<
  TData = Awaited<ReturnType<typeof deletePackage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeletePackagePathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof deletePackage>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof deletePackage>>,
          TError,
          Awaited<ReturnType<typeof deletePackage>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeletePackage<
  TData = Awaited<ReturnType<typeof deletePackage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeletePackagePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deletePackage>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof deletePackage>>,
          TError,
          Awaited<ReturnType<typeof deletePackage>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeletePackage<
  TData = Awaited<ReturnType<typeof deletePackage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeletePackagePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deletePackage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Delete Package
 */

export function useDeletePackage<
  TData = Awaited<ReturnType<typeof deletePackage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { packageId }: DeletePackagePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deletePackage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getDeletePackageQueryOptions({ packageId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Delete Package
 */
export const prefetchDeletePackageQuery = async <
  TData = Awaited<ReturnType<typeof deletePackage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { packageId }: DeletePackagePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deletePackage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getDeletePackageQueryOptions({ packageId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Delete Package
 */
export const invalidateDeletePackage = async (
  queryClient: QueryClient,
  { packageId }: DeletePackagePathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getDeletePackageQueryKey({ packageId }) }, options);

  return queryClient;
};

/**
 * @summary Get Package
 */
export const getPackage = (
  { packageId }: GetPackagePathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLPackageDetailResponse>({ url: `/kb/packages/${packageId}`, method: 'GET', signal }, options);
};

export const getGetPackageQueryKey = ({ packageId }: GetPackagePathParameters) => {
  return ['kb', 'packages', packageId] as const;
};

export const getGetPackageQueryOptions = <
  TData = Awaited<ReturnType<typeof getPackage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { packageId }: GetPackagePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPackage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetPackageQueryKey({ packageId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getPackage>>> = ({ signal }) =>
    getPackage({ packageId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: packageId !== null && packageId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getPackage>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetPackageQueryResult = NonNullable<Awaited<ReturnType<typeof getPackage>>>;
export type GetPackageQueryError = ErrorType<TLHTTPValidationError>;

export function useGetPackage<
  TData = Awaited<ReturnType<typeof getPackage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetPackagePathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPackage>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getPackage>>,
          TError,
          Awaited<ReturnType<typeof getPackage>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetPackage<
  TData = Awaited<ReturnType<typeof getPackage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetPackagePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPackage>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getPackage>>,
          TError,
          Awaited<ReturnType<typeof getPackage>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetPackage<
  TData = Awaited<ReturnType<typeof getPackage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetPackagePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPackage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Package
 */

export function useGetPackage<
  TData = Awaited<ReturnType<typeof getPackage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { packageId }: GetPackagePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPackage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetPackageQueryOptions({ packageId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Package
 */
export const prefetchGetPackageQuery = async <
  TData = Awaited<ReturnType<typeof getPackage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { packageId }: GetPackagePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPackage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetPackageQueryOptions({ packageId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Package
 */
export const invalidateGetPackage = async (
  queryClient: QueryClient,
  { packageId }: GetPackagePathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetPackageQueryKey({ packageId }) }, options);

  return queryClient;
};

/**
 * @summary Update Package
 */
export const updatePackage = (
  { packageId }: UpdatePackagePathParameters,
  tLUpdatePackageRequest: BodyType<TLUpdatePackageRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLPackageResponse>(
    {
      url: `/kb/packages/${packageId}`,
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      data: tLUpdatePackageRequest,
      signal,
    },
    options,
  );
};

export const getUpdatePackageQueryKey = (
  { packageId }: UpdatePackagePathParameters,
  tLUpdatePackageRequest?: BodyType<TLUpdatePackageRequest>,
) => {
  return ['PATCH', 'kb', 'packages', packageId, tLUpdatePackageRequest] as const;
};

export const getUpdatePackageQueryOptions = <
  TData = Awaited<ReturnType<typeof updatePackage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { packageId }: UpdatePackagePathParameters,
  tLUpdatePackageRequest: BodyType<TLUpdatePackageRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updatePackage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getUpdatePackageQueryKey({ packageId }, tLUpdatePackageRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof updatePackage>>> = ({ signal }) =>
    updatePackage({ packageId }, tLUpdatePackageRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: packageId !== null && packageId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof updatePackage>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type UpdatePackageQueryResult = NonNullable<Awaited<ReturnType<typeof updatePackage>>>;
export type UpdatePackageQueryError = ErrorType<TLHTTPValidationError>;

export function useUpdatePackage<
  TData = Awaited<ReturnType<typeof updatePackage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdatePackagePathParameters,
  tLUpdatePackageRequest: BodyType<TLUpdatePackageRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof updatePackage>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof updatePackage>>,
          TError,
          Awaited<ReturnType<typeof updatePackage>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdatePackage<
  TData = Awaited<ReturnType<typeof updatePackage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdatePackagePathParameters,
  tLUpdatePackageRequest: BodyType<TLUpdatePackageRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updatePackage>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof updatePackage>>,
          TError,
          Awaited<ReturnType<typeof updatePackage>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdatePackage<
  TData = Awaited<ReturnType<typeof updatePackage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdatePackagePathParameters,
  tLUpdatePackageRequest: BodyType<TLUpdatePackageRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updatePackage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Update Package
 */

export function useUpdatePackage<
  TData = Awaited<ReturnType<typeof updatePackage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { packageId }: UpdatePackagePathParameters,
  tLUpdatePackageRequest: BodyType<TLUpdatePackageRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updatePackage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getUpdatePackageQueryOptions({ packageId }, tLUpdatePackageRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Update Package
 */
export const prefetchUpdatePackageQuery = async <
  TData = Awaited<ReturnType<typeof updatePackage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { packageId }: UpdatePackagePathParameters,
  tLUpdatePackageRequest: BodyType<TLUpdatePackageRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updatePackage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getUpdatePackageQueryOptions({ packageId }, tLUpdatePackageRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Update Package
 */
export const invalidateUpdatePackage = async (
  queryClient: QueryClient,
  { packageId }: UpdatePackagePathParameters,
  tLUpdatePackageRequest: BodyType<TLUpdatePackageRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getUpdatePackageQueryKey({ packageId }, tLUpdatePackageRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Accept Review
 */
export const acceptReview = (
  { packageId }: AcceptReviewPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLPackageResponse>({ url: `/kb/packages/${packageId}/accept`, method: 'POST', signal }, options);
};

export const getAcceptReviewQueryKey = ({ packageId }: AcceptReviewPathParameters) => {
  return ['POST', 'kb', 'packages', packageId, 'accept'] as const;
};

export const getAcceptReviewQueryOptions = <
  TData = Awaited<ReturnType<typeof acceptReview>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { packageId }: AcceptReviewPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof acceptReview>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getAcceptReviewQueryKey({ packageId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof acceptReview>>> = ({ signal }) =>
    acceptReview({ packageId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: packageId !== null && packageId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof acceptReview>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type AcceptReviewQueryResult = NonNullable<Awaited<ReturnType<typeof acceptReview>>>;
export type AcceptReviewQueryError = ErrorType<TLHTTPValidationError>;

export function useAcceptReview<
  TData = Awaited<ReturnType<typeof acceptReview>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: AcceptReviewPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof acceptReview>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof acceptReview>>,
          TError,
          Awaited<ReturnType<typeof acceptReview>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useAcceptReview<
  TData = Awaited<ReturnType<typeof acceptReview>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: AcceptReviewPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof acceptReview>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof acceptReview>>,
          TError,
          Awaited<ReturnType<typeof acceptReview>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useAcceptReview<
  TData = Awaited<ReturnType<typeof acceptReview>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: AcceptReviewPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof acceptReview>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Accept Review
 */

export function useAcceptReview<
  TData = Awaited<ReturnType<typeof acceptReview>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { packageId }: AcceptReviewPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof acceptReview>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getAcceptReviewQueryOptions({ packageId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Accept Review
 */
export const prefetchAcceptReviewQuery = async <
  TData = Awaited<ReturnType<typeof acceptReview>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { packageId }: AcceptReviewPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof acceptReview>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getAcceptReviewQueryOptions({ packageId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Accept Review
 */
export const invalidateAcceptReview = async (
  queryClient: QueryClient,
  { packageId }: AcceptReviewPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getAcceptReviewQueryKey({ packageId }) }, options);

  return queryClient;
};

/**
 * @summary Approve Package
 */
export const approvePackage = (
  { packageId }: ApprovePackagePathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLPackageResponse>({ url: `/kb/packages/${packageId}/approve`, method: 'POST', signal }, options);
};

export const getApprovePackageQueryKey = ({ packageId }: ApprovePackagePathParameters) => {
  return ['POST', 'kb', 'packages', packageId, 'approve'] as const;
};

export const getApprovePackageQueryOptions = <
  TData = Awaited<ReturnType<typeof approvePackage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { packageId }: ApprovePackagePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof approvePackage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getApprovePackageQueryKey({ packageId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof approvePackage>>> = ({ signal }) =>
    approvePackage({ packageId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: packageId !== null && packageId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof approvePackage>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type ApprovePackageQueryResult = NonNullable<Awaited<ReturnType<typeof approvePackage>>>;
export type ApprovePackageQueryError = ErrorType<TLHTTPValidationError>;

export function useApprovePackage<
  TData = Awaited<ReturnType<typeof approvePackage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ApprovePackagePathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof approvePackage>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof approvePackage>>,
          TError,
          Awaited<ReturnType<typeof approvePackage>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useApprovePackage<
  TData = Awaited<ReturnType<typeof approvePackage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ApprovePackagePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof approvePackage>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof approvePackage>>,
          TError,
          Awaited<ReturnType<typeof approvePackage>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useApprovePackage<
  TData = Awaited<ReturnType<typeof approvePackage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ApprovePackagePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof approvePackage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Approve Package
 */

export function useApprovePackage<
  TData = Awaited<ReturnType<typeof approvePackage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { packageId }: ApprovePackagePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof approvePackage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getApprovePackageQueryOptions({ packageId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Approve Package
 */
export const prefetchApprovePackageQuery = async <
  TData = Awaited<ReturnType<typeof approvePackage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { packageId }: ApprovePackagePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof approvePackage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getApprovePackageQueryOptions({ packageId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Approve Package
 */
export const invalidateApprovePackage = async (
  queryClient: QueryClient,
  { packageId }: ApprovePackagePathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getApprovePackageQueryKey({ packageId }) }, options);

  return queryClient;
};

/**
 * @summary Add Comment
 */
export const addComment = (
  { packageId }: AddCommentPathParameters,
  tLCommentRequest: BodyType<TLCommentRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLHistoryEntryResponse>(
    {
      url: `/kb/packages/${packageId}/comment`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLCommentRequest,
      signal,
    },
    options,
  );
};

export const getAddCommentQueryKey = (
  { packageId }: AddCommentPathParameters,
  tLCommentRequest?: BodyType<TLCommentRequest>,
) => {
  return ['POST', 'kb', 'packages', packageId, 'comment', tLCommentRequest] as const;
};

export const getAddCommentQueryOptions = <
  TData = Awaited<ReturnType<typeof addComment>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { packageId }: AddCommentPathParameters,
  tLCommentRequest: BodyType<TLCommentRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof addComment>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getAddCommentQueryKey({ packageId }, tLCommentRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof addComment>>> = ({ signal }) =>
    addComment({ packageId }, tLCommentRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: packageId !== null && packageId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof addComment>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type AddCommentQueryResult = NonNullable<Awaited<ReturnType<typeof addComment>>>;
export type AddCommentQueryError = ErrorType<TLHTTPValidationError>;

export function useAddComment<
  TData = Awaited<ReturnType<typeof addComment>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: AddCommentPathParameters,
  tLCommentRequest: BodyType<TLCommentRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof addComment>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof addComment>>,
          TError,
          Awaited<ReturnType<typeof addComment>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useAddComment<
  TData = Awaited<ReturnType<typeof addComment>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: AddCommentPathParameters,
  tLCommentRequest: BodyType<TLCommentRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof addComment>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof addComment>>,
          TError,
          Awaited<ReturnType<typeof addComment>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useAddComment<
  TData = Awaited<ReturnType<typeof addComment>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: AddCommentPathParameters,
  tLCommentRequest: BodyType<TLCommentRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof addComment>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Add Comment
 */

export function useAddComment<
  TData = Awaited<ReturnType<typeof addComment>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { packageId }: AddCommentPathParameters,
  tLCommentRequest: BodyType<TLCommentRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof addComment>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getAddCommentQueryOptions({ packageId }, tLCommentRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Add Comment
 */
export const prefetchAddCommentQuery = async <
  TData = Awaited<ReturnType<typeof addComment>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { packageId }: AddCommentPathParameters,
  tLCommentRequest: BodyType<TLCommentRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof addComment>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getAddCommentQueryOptions({ packageId }, tLCommentRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Add Comment
 */
export const invalidateAddComment = async (
  queryClient: QueryClient,
  { packageId }: AddCommentPathParameters,
  tLCommentRequest: BodyType<TLCommentRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getAddCommentQueryKey({ packageId }, tLCommentRequest) }, options);

  return queryClient;
};

/**
 * A queued response is identified by queued_message_id when another turn is in flight.
 * @summary Send Kb Message
 */
export const sendKbMessage = (
  { packageId }: SendKbMessagePathParameters,
  tLKbSendMessageRequest: BodyType<TLKbSendMessageRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLChatMessageResponse | TLQueuedMessageResponse>(
    {
      url: `/kb/packages/${packageId}/conversations/messages`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLKbSendMessageRequest,
      signal,
    },
    options,
  );
};

export const getSendKbMessageQueryKey = (
  { packageId }: SendKbMessagePathParameters,
  tLKbSendMessageRequest?: BodyType<TLKbSendMessageRequest>,
) => {
  return ['POST', 'kb', 'packages', packageId, 'conversations', 'messages', tLKbSendMessageRequest] as const;
};

export const getSendKbMessageQueryOptions = <
  TData = Awaited<ReturnType<typeof sendKbMessage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { packageId }: SendKbMessagePathParameters,
  tLKbSendMessageRequest: BodyType<TLKbSendMessageRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof sendKbMessage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getSendKbMessageQueryKey({ packageId }, tLKbSendMessageRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof sendKbMessage>>> = ({ signal }) =>
    sendKbMessage({ packageId }, tLKbSendMessageRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: packageId !== null && packageId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof sendKbMessage>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type SendKbMessageQueryResult = NonNullable<Awaited<ReturnType<typeof sendKbMessage>>>;
export type SendKbMessageQueryError = ErrorType<TLHTTPValidationError>;

export function useSendKbMessage<
  TData = Awaited<ReturnType<typeof sendKbMessage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: SendKbMessagePathParameters,
  tLKbSendMessageRequest: BodyType<TLKbSendMessageRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof sendKbMessage>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof sendKbMessage>>,
          TError,
          Awaited<ReturnType<typeof sendKbMessage>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useSendKbMessage<
  TData = Awaited<ReturnType<typeof sendKbMessage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: SendKbMessagePathParameters,
  tLKbSendMessageRequest: BodyType<TLKbSendMessageRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof sendKbMessage>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof sendKbMessage>>,
          TError,
          Awaited<ReturnType<typeof sendKbMessage>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useSendKbMessage<
  TData = Awaited<ReturnType<typeof sendKbMessage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: SendKbMessagePathParameters,
  tLKbSendMessageRequest: BodyType<TLKbSendMessageRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof sendKbMessage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Send Kb Message
 */

export function useSendKbMessage<
  TData = Awaited<ReturnType<typeof sendKbMessage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { packageId }: SendKbMessagePathParameters,
  tLKbSendMessageRequest: BodyType<TLKbSendMessageRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof sendKbMessage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getSendKbMessageQueryOptions({ packageId }, tLKbSendMessageRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Send Kb Message
 */
export const prefetchSendKbMessageQuery = async <
  TData = Awaited<ReturnType<typeof sendKbMessage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { packageId }: SendKbMessagePathParameters,
  tLKbSendMessageRequest: BodyType<TLKbSendMessageRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof sendKbMessage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getSendKbMessageQueryOptions({ packageId }, tLKbSendMessageRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Send Kb Message
 */
export const invalidateSendKbMessage = async (
  queryClient: QueryClient,
  { packageId }: SendKbMessagePathParameters,
  tLKbSendMessageRequest: BodyType<TLKbSendMessageRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getSendKbMessageQueryKey({ packageId }, tLKbSendMessageRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary List Documents
 */
export const listDocuments = (
  { packageId }: ListDocumentsPathParameters,
  params?: ListDocumentsParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLPaginatedResponseDocumentResponse>(
    { url: `/kb/packages/${packageId}/documents`, method: 'GET', params, signal },
    options,
  );
};

export const getListDocumentsQueryKey = ({ packageId }: ListDocumentsPathParameters, params?: ListDocumentsParams) => {
  return ['kb', 'packages', packageId, 'documents', ...(params ? [params] : [])] as const;
};

export const getListDocumentsQueryOptions = <
  TData = Awaited<ReturnType<typeof listDocuments>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { packageId }: ListDocumentsPathParameters,
  params?: ListDocumentsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listDocuments>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListDocumentsQueryKey({ packageId }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listDocuments>>> = ({ signal }) =>
    listDocuments({ packageId }, params, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: packageId !== null && packageId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof listDocuments>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type ListDocumentsQueryResult = NonNullable<Awaited<ReturnType<typeof listDocuments>>>;
export type ListDocumentsQueryError = ErrorType<TLHTTPValidationError>;

export function useListDocuments<
  TData = Awaited<ReturnType<typeof listDocuments>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListDocumentsPathParameters,
  params: undefined | ListDocumentsParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof listDocuments>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listDocuments>>,
          TError,
          Awaited<ReturnType<typeof listDocuments>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListDocuments<
  TData = Awaited<ReturnType<typeof listDocuments>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListDocumentsPathParameters,
  params?: ListDocumentsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listDocuments>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listDocuments>>,
          TError,
          Awaited<ReturnType<typeof listDocuments>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListDocuments<
  TData = Awaited<ReturnType<typeof listDocuments>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListDocumentsPathParameters,
  params?: ListDocumentsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listDocuments>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List Documents
 */

export function useListDocuments<
  TData = Awaited<ReturnType<typeof listDocuments>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { packageId }: ListDocumentsPathParameters,
  params?: ListDocumentsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listDocuments>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListDocumentsQueryOptions({ packageId }, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary List Documents
 */
export const prefetchListDocumentsQuery = async <
  TData = Awaited<ReturnType<typeof listDocuments>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { packageId }: ListDocumentsPathParameters,
  params?: ListDocumentsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listDocuments>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getListDocumentsQueryOptions({ packageId }, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary List Documents
 */
export const invalidateListDocuments = async (
  queryClient: QueryClient,
  { packageId }: ListDocumentsPathParameters,
  params?: ListDocumentsParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getListDocumentsQueryKey({ packageId }, params) }, options);

  return queryClient;
};

/**
 * @summary Add Document
 */
export const addDocument = (
  { packageId }: AddDocumentPathParameters,
  tLAddDocumentPackagesRequest: BodyType<TLAddDocumentPackagesRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLAddDocumentResponse>(
    {
      url: `/kb/packages/${packageId}/documents`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLAddDocumentPackagesRequest,
      signal,
    },
    options,
  );
};

export const getAddDocumentQueryKey = (
  { packageId }: AddDocumentPathParameters,
  tLAddDocumentPackagesRequest?: BodyType<TLAddDocumentPackagesRequest>,
) => {
  return ['POST', 'kb', 'packages', packageId, 'documents', tLAddDocumentPackagesRequest] as const;
};

export const getAddDocumentQueryOptions = <
  TData = Awaited<ReturnType<typeof addDocument>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { packageId }: AddDocumentPathParameters,
  tLAddDocumentPackagesRequest: BodyType<TLAddDocumentPackagesRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof addDocument>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getAddDocumentQueryKey({ packageId }, tLAddDocumentPackagesRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof addDocument>>> = ({ signal }) =>
    addDocument({ packageId }, tLAddDocumentPackagesRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: packageId !== null && packageId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof addDocument>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type AddDocumentQueryResult = NonNullable<Awaited<ReturnType<typeof addDocument>>>;
export type AddDocumentQueryError = ErrorType<TLHTTPValidationError>;

export function useAddDocument<
  TData = Awaited<ReturnType<typeof addDocument>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: AddDocumentPathParameters,
  tLAddDocumentPackagesRequest: BodyType<TLAddDocumentPackagesRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof addDocument>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof addDocument>>,
          TError,
          Awaited<ReturnType<typeof addDocument>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useAddDocument<
  TData = Awaited<ReturnType<typeof addDocument>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: AddDocumentPathParameters,
  tLAddDocumentPackagesRequest: BodyType<TLAddDocumentPackagesRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof addDocument>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof addDocument>>,
          TError,
          Awaited<ReturnType<typeof addDocument>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useAddDocument<
  TData = Awaited<ReturnType<typeof addDocument>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: AddDocumentPathParameters,
  tLAddDocumentPackagesRequest: BodyType<TLAddDocumentPackagesRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof addDocument>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Add Document
 */

export function useAddDocument<
  TData = Awaited<ReturnType<typeof addDocument>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { packageId }: AddDocumentPathParameters,
  tLAddDocumentPackagesRequest: BodyType<TLAddDocumentPackagesRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof addDocument>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getAddDocumentQueryOptions({ packageId }, tLAddDocumentPackagesRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Add Document
 */
export const prefetchAddDocumentQuery = async <
  TData = Awaited<ReturnType<typeof addDocument>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { packageId }: AddDocumentPathParameters,
  tLAddDocumentPackagesRequest: BodyType<TLAddDocumentPackagesRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof addDocument>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getAddDocumentQueryOptions({ packageId }, tLAddDocumentPackagesRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Add Document
 */
export const invalidateAddDocument = async (
  queryClient: QueryClient,
  { packageId }: AddDocumentPathParameters,
  tLAddDocumentPackagesRequest: BodyType<TLAddDocumentPackagesRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getAddDocumentQueryKey({ packageId }, tLAddDocumentPackagesRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Remove Document
 */
export const removeDocument = (
  { packageId, documentId }: RemoveDocumentPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<void>(
    { url: `/kb/packages/${packageId}/documents/${documentId}`, method: 'DELETE', signal },
    options,
  );
};

export const getRemoveDocumentQueryKey = ({ packageId, documentId }: RemoveDocumentPathParameters) => {
  return ['DELETE', 'kb', 'packages', packageId, 'documents', documentId] as const;
};

export const getRemoveDocumentQueryOptions = <
  TData = Awaited<ReturnType<typeof removeDocument>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { packageId, documentId }: RemoveDocumentPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof removeDocument>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getRemoveDocumentQueryKey({ packageId, documentId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof removeDocument>>> = ({ signal }) =>
    removeDocument({ packageId, documentId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: packageId !== null && packageId !== undefined && documentId !== null && documentId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof removeDocument>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type RemoveDocumentQueryResult = NonNullable<Awaited<ReturnType<typeof removeDocument>>>;
export type RemoveDocumentQueryError = ErrorType<TLHTTPValidationError>;

export function useRemoveDocument<
  TData = Awaited<ReturnType<typeof removeDocument>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RemoveDocumentPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof removeDocument>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof removeDocument>>,
          TError,
          Awaited<ReturnType<typeof removeDocument>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRemoveDocument<
  TData = Awaited<ReturnType<typeof removeDocument>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RemoveDocumentPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof removeDocument>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof removeDocument>>,
          TError,
          Awaited<ReturnType<typeof removeDocument>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRemoveDocument<
  TData = Awaited<ReturnType<typeof removeDocument>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RemoveDocumentPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof removeDocument>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Remove Document
 */

export function useRemoveDocument<
  TData = Awaited<ReturnType<typeof removeDocument>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { packageId, documentId }: RemoveDocumentPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof removeDocument>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getRemoveDocumentQueryOptions({ packageId, documentId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Remove Document
 */
export const prefetchRemoveDocumentQuery = async <
  TData = Awaited<ReturnType<typeof removeDocument>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { packageId, documentId }: RemoveDocumentPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof removeDocument>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getRemoveDocumentQueryOptions({ packageId, documentId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Remove Document
 */
export const invalidateRemoveDocument = async (
  queryClient: QueryClient,
  { packageId, documentId }: RemoveDocumentPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getRemoveDocumentQueryKey({ packageId, documentId }) }, options);

  return queryClient;
};

/**
 * @summary Get Package History
 */
export const getPackageHistory = (
  { packageId }: GetPackageHistoryPathParameters,
  params?: GetPackageHistoryParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLPaginatedResponseHistoryEntryResponse>(
    { url: `/kb/packages/${packageId}/history`, method: 'GET', params, signal },
    options,
  );
};

export const getGetPackageHistoryQueryKey = (
  { packageId }: GetPackageHistoryPathParameters,
  params?: GetPackageHistoryParams,
) => {
  return ['kb', 'packages', packageId, 'history', ...(params ? [params] : [])] as const;
};

export const getGetPackageHistoryQueryOptions = <
  TData = Awaited<ReturnType<typeof getPackageHistory>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { packageId }: GetPackageHistoryPathParameters,
  params?: GetPackageHistoryParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPackageHistory>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetPackageHistoryQueryKey({ packageId }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getPackageHistory>>> = ({ signal }) =>
    getPackageHistory({ packageId }, params, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: packageId !== null && packageId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getPackageHistory>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetPackageHistoryQueryResult = NonNullable<Awaited<ReturnType<typeof getPackageHistory>>>;
export type GetPackageHistoryQueryError = ErrorType<TLHTTPValidationError>;

export function useGetPackageHistory<
  TData = Awaited<ReturnType<typeof getPackageHistory>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetPackageHistoryPathParameters,
  params: undefined | GetPackageHistoryParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPackageHistory>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getPackageHistory>>,
          TError,
          Awaited<ReturnType<typeof getPackageHistory>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetPackageHistory<
  TData = Awaited<ReturnType<typeof getPackageHistory>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetPackageHistoryPathParameters,
  params?: GetPackageHistoryParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPackageHistory>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getPackageHistory>>,
          TError,
          Awaited<ReturnType<typeof getPackageHistory>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetPackageHistory<
  TData = Awaited<ReturnType<typeof getPackageHistory>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetPackageHistoryPathParameters,
  params?: GetPackageHistoryParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPackageHistory>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Package History
 */

export function useGetPackageHistory<
  TData = Awaited<ReturnType<typeof getPackageHistory>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { packageId }: GetPackageHistoryPathParameters,
  params?: GetPackageHistoryParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPackageHistory>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetPackageHistoryQueryOptions({ packageId }, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Package History
 */
export const prefetchGetPackageHistoryQuery = async <
  TData = Awaited<ReturnType<typeof getPackageHistory>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { packageId }: GetPackageHistoryPathParameters,
  params?: GetPackageHistoryParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPackageHistory>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetPackageHistoryQueryOptions({ packageId }, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Package History
 */
export const invalidateGetPackageHistory = async (
  queryClient: QueryClient,
  { packageId }: GetPackageHistoryPathParameters,
  params?: GetPackageHistoryParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetPackageHistoryQueryKey({ packageId }, params) }, options);

  return queryClient;
};

/**
 * @summary Reject Package
 */
export const rejectPackage = (
  { packageId }: RejectPackagePathParameters,
  tLRejectPackageRequest: BodyType<TLRejectPackageRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLPackageResponse>(
    {
      url: `/kb/packages/${packageId}/reject`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLRejectPackageRequest,
      signal,
    },
    options,
  );
};

export const getRejectPackageQueryKey = (
  { packageId }: RejectPackagePathParameters,
  tLRejectPackageRequest?: BodyType<TLRejectPackageRequest>,
) => {
  return ['POST', 'kb', 'packages', packageId, 'reject', tLRejectPackageRequest] as const;
};

export const getRejectPackageQueryOptions = <
  TData = Awaited<ReturnType<typeof rejectPackage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { packageId }: RejectPackagePathParameters,
  tLRejectPackageRequest: BodyType<TLRejectPackageRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof rejectPackage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getRejectPackageQueryKey({ packageId }, tLRejectPackageRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof rejectPackage>>> = ({ signal }) =>
    rejectPackage({ packageId }, tLRejectPackageRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: packageId !== null && packageId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof rejectPackage>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type RejectPackageQueryResult = NonNullable<Awaited<ReturnType<typeof rejectPackage>>>;
export type RejectPackageQueryError = ErrorType<TLHTTPValidationError>;

export function useRejectPackage<
  TData = Awaited<ReturnType<typeof rejectPackage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RejectPackagePathParameters,
  tLRejectPackageRequest: BodyType<TLRejectPackageRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof rejectPackage>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof rejectPackage>>,
          TError,
          Awaited<ReturnType<typeof rejectPackage>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRejectPackage<
  TData = Awaited<ReturnType<typeof rejectPackage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RejectPackagePathParameters,
  tLRejectPackageRequest: BodyType<TLRejectPackageRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof rejectPackage>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof rejectPackage>>,
          TError,
          Awaited<ReturnType<typeof rejectPackage>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRejectPackage<
  TData = Awaited<ReturnType<typeof rejectPackage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RejectPackagePathParameters,
  tLRejectPackageRequest: BodyType<TLRejectPackageRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof rejectPackage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Reject Package
 */

export function useRejectPackage<
  TData = Awaited<ReturnType<typeof rejectPackage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { packageId }: RejectPackagePathParameters,
  tLRejectPackageRequest: BodyType<TLRejectPackageRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof rejectPackage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getRejectPackageQueryOptions({ packageId }, tLRejectPackageRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Reject Package
 */
export const prefetchRejectPackageQuery = async <
  TData = Awaited<ReturnType<typeof rejectPackage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { packageId }: RejectPackagePathParameters,
  tLRejectPackageRequest: BodyType<TLRejectPackageRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof rejectPackage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getRejectPackageQueryOptions({ packageId }, tLRejectPackageRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Reject Package
 */
export const invalidateRejectPackage = async (
  queryClient: QueryClient,
  { packageId }: RejectPackagePathParameters,
  tLRejectPackageRequest: BodyType<TLRejectPackageRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getRejectPackageQueryKey({ packageId }, tLRejectPackageRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Retry Kb Package Promotion
 */
export const retryKbPackagePromotion = (
  { packageId }: RetryKbPackagePromotionPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLPackageResponse>(
    { url: `/kb/packages/${packageId}/retry-promotion`, method: 'POST', signal },
    options,
  );
};

export const getRetryKbPackagePromotionQueryKey = ({ packageId }: RetryKbPackagePromotionPathParameters) => {
  return ['POST', 'kb', 'packages', packageId, 'retry-promotion'] as const;
};

export const getRetryKbPackagePromotionQueryOptions = <
  TData = Awaited<ReturnType<typeof retryKbPackagePromotion>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { packageId }: RetryKbPackagePromotionPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof retryKbPackagePromotion>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getRetryKbPackagePromotionQueryKey({ packageId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof retryKbPackagePromotion>>> = ({ signal }) =>
    retryKbPackagePromotion({ packageId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: packageId !== null && packageId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof retryKbPackagePromotion>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type RetryKbPackagePromotionQueryResult = NonNullable<Awaited<ReturnType<typeof retryKbPackagePromotion>>>;
export type RetryKbPackagePromotionQueryError = ErrorType<TLHTTPValidationError>;

export function useRetryKbPackagePromotion<
  TData = Awaited<ReturnType<typeof retryKbPackagePromotion>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RetryKbPackagePromotionPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof retryKbPackagePromotion>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof retryKbPackagePromotion>>,
          TError,
          Awaited<ReturnType<typeof retryKbPackagePromotion>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRetryKbPackagePromotion<
  TData = Awaited<ReturnType<typeof retryKbPackagePromotion>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RetryKbPackagePromotionPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof retryKbPackagePromotion>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof retryKbPackagePromotion>>,
          TError,
          Awaited<ReturnType<typeof retryKbPackagePromotion>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRetryKbPackagePromotion<
  TData = Awaited<ReturnType<typeof retryKbPackagePromotion>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RetryKbPackagePromotionPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof retryKbPackagePromotion>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Retry Kb Package Promotion
 */

export function useRetryKbPackagePromotion<
  TData = Awaited<ReturnType<typeof retryKbPackagePromotion>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { packageId }: RetryKbPackagePromotionPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof retryKbPackagePromotion>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getRetryKbPackagePromotionQueryOptions({ packageId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Retry Kb Package Promotion
 */
export const prefetchRetryKbPackagePromotionQuery = async <
  TData = Awaited<ReturnType<typeof retryKbPackagePromotion>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { packageId }: RetryKbPackagePromotionPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof retryKbPackagePromotion>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getRetryKbPackagePromotionQueryOptions({ packageId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Retry Kb Package Promotion
 */
export const invalidateRetryKbPackagePromotion = async (
  queryClient: QueryClient,
  { packageId }: RetryKbPackagePromotionPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getRetryKbPackagePromotionQueryKey({ packageId }) }, options);

  return queryClient;
};

/**
 * @summary Search Package
 */
export const searchPackage = (
  { packageId }: SearchPackagePathParameters,
  tLKbSearchRequest: BodyType<TLKbSearchRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLKbSearchResponse>(
    {
      url: `/kb/packages/${packageId}/search`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLKbSearchRequest,
      signal,
    },
    options,
  );
};

export const getSearchPackageQueryKey = (
  { packageId }: SearchPackagePathParameters,
  tLKbSearchRequest?: BodyType<TLKbSearchRequest>,
) => {
  return ['POST', 'kb', 'packages', packageId, 'search', tLKbSearchRequest] as const;
};

export const getSearchPackageQueryOptions = <
  TData = Awaited<ReturnType<typeof searchPackage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { packageId }: SearchPackagePathParameters,
  tLKbSearchRequest: BodyType<TLKbSearchRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof searchPackage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getSearchPackageQueryKey({ packageId }, tLKbSearchRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof searchPackage>>> = ({ signal }) =>
    searchPackage({ packageId }, tLKbSearchRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: packageId !== null && packageId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof searchPackage>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type SearchPackageQueryResult = NonNullable<Awaited<ReturnType<typeof searchPackage>>>;
export type SearchPackageQueryError = ErrorType<TLHTTPValidationError>;

export function useSearchPackage<
  TData = Awaited<ReturnType<typeof searchPackage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: SearchPackagePathParameters,
  tLKbSearchRequest: BodyType<TLKbSearchRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof searchPackage>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof searchPackage>>,
          TError,
          Awaited<ReturnType<typeof searchPackage>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useSearchPackage<
  TData = Awaited<ReturnType<typeof searchPackage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: SearchPackagePathParameters,
  tLKbSearchRequest: BodyType<TLKbSearchRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof searchPackage>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof searchPackage>>,
          TError,
          Awaited<ReturnType<typeof searchPackage>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useSearchPackage<
  TData = Awaited<ReturnType<typeof searchPackage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: SearchPackagePathParameters,
  tLKbSearchRequest: BodyType<TLKbSearchRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof searchPackage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Search Package
 */

export function useSearchPackage<
  TData = Awaited<ReturnType<typeof searchPackage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { packageId }: SearchPackagePathParameters,
  tLKbSearchRequest: BodyType<TLKbSearchRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof searchPackage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getSearchPackageQueryOptions({ packageId }, tLKbSearchRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Search Package
 */
export const prefetchSearchPackageQuery = async <
  TData = Awaited<ReturnType<typeof searchPackage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { packageId }: SearchPackagePathParameters,
  tLKbSearchRequest: BodyType<TLKbSearchRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof searchPackage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getSearchPackageQueryOptions({ packageId }, tLKbSearchRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Search Package
 */
export const invalidateSearchPackage = async (
  queryClient: QueryClient,
  { packageId }: SearchPackagePathParameters,
  tLKbSearchRequest: BodyType<TLKbSearchRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getSearchPackageQueryKey({ packageId }, tLKbSearchRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Submit Package
 */
export const submitPackage = (
  { packageId }: SubmitPackagePathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLPackageResponse>({ url: `/kb/packages/${packageId}/submit`, method: 'POST', signal }, options);
};

export const getSubmitPackageQueryKey = ({ packageId }: SubmitPackagePathParameters) => {
  return ['POST', 'kb', 'packages', packageId, 'submit'] as const;
};

export const getSubmitPackageQueryOptions = <
  TData = Awaited<ReturnType<typeof submitPackage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { packageId }: SubmitPackagePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof submitPackage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getSubmitPackageQueryKey({ packageId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof submitPackage>>> = ({ signal }) =>
    submitPackage({ packageId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: packageId !== null && packageId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof submitPackage>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type SubmitPackageQueryResult = NonNullable<Awaited<ReturnType<typeof submitPackage>>>;
export type SubmitPackageQueryError = ErrorType<TLHTTPValidationError>;

export function useSubmitPackage<
  TData = Awaited<ReturnType<typeof submitPackage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: SubmitPackagePathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof submitPackage>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof submitPackage>>,
          TError,
          Awaited<ReturnType<typeof submitPackage>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useSubmitPackage<
  TData = Awaited<ReturnType<typeof submitPackage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: SubmitPackagePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof submitPackage>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof submitPackage>>,
          TError,
          Awaited<ReturnType<typeof submitPackage>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useSubmitPackage<
  TData = Awaited<ReturnType<typeof submitPackage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: SubmitPackagePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof submitPackage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Submit Package
 */

export function useSubmitPackage<
  TData = Awaited<ReturnType<typeof submitPackage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { packageId }: SubmitPackagePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof submitPackage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getSubmitPackageQueryOptions({ packageId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Submit Package
 */
export const prefetchSubmitPackageQuery = async <
  TData = Awaited<ReturnType<typeof submitPackage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { packageId }: SubmitPackagePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof submitPackage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getSubmitPackageQueryOptions({ packageId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Submit Package
 */
export const invalidateSubmitPackage = async (
  queryClient: QueryClient,
  { packageId }: SubmitPackagePathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getSubmitPackageQueryKey({ packageId }) }, options);

  return queryClient;
};

/**
 * @summary Get Package Suggested Prompts
 */
export const getPackageSuggestedPrompts = (
  { packageId }: GetPackageSuggestedPromptsPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLKbSuggestedPromptsResponse>(
    { url: `/kb/packages/${packageId}/suggested-prompts`, method: 'GET', signal },
    options,
  );
};

export const getGetPackageSuggestedPromptsQueryKey = ({ packageId }: GetPackageSuggestedPromptsPathParameters) => {
  return ['kb', 'packages', packageId, 'suggested-prompts'] as const;
};

export const getGetPackageSuggestedPromptsQueryOptions = <
  TData = Awaited<ReturnType<typeof getPackageSuggestedPrompts>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { packageId }: GetPackageSuggestedPromptsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPackageSuggestedPrompts>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetPackageSuggestedPromptsQueryKey({ packageId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getPackageSuggestedPrompts>>> = ({ signal }) =>
    getPackageSuggestedPrompts({ packageId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: packageId !== null && packageId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getPackageSuggestedPrompts>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetPackageSuggestedPromptsQueryResult = NonNullable<Awaited<ReturnType<typeof getPackageSuggestedPrompts>>>;
export type GetPackageSuggestedPromptsQueryError = ErrorType<TLHTTPValidationError>;

export function useGetPackageSuggestedPrompts<
  TData = Awaited<ReturnType<typeof getPackageSuggestedPrompts>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetPackageSuggestedPromptsPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPackageSuggestedPrompts>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getPackageSuggestedPrompts>>,
          TError,
          Awaited<ReturnType<typeof getPackageSuggestedPrompts>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetPackageSuggestedPrompts<
  TData = Awaited<ReturnType<typeof getPackageSuggestedPrompts>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetPackageSuggestedPromptsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPackageSuggestedPrompts>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getPackageSuggestedPrompts>>,
          TError,
          Awaited<ReturnType<typeof getPackageSuggestedPrompts>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetPackageSuggestedPrompts<
  TData = Awaited<ReturnType<typeof getPackageSuggestedPrompts>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetPackageSuggestedPromptsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPackageSuggestedPrompts>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Package Suggested Prompts
 */

export function useGetPackageSuggestedPrompts<
  TData = Awaited<ReturnType<typeof getPackageSuggestedPrompts>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { packageId }: GetPackageSuggestedPromptsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPackageSuggestedPrompts>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetPackageSuggestedPromptsQueryOptions({ packageId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Package Suggested Prompts
 */
export const prefetchGetPackageSuggestedPromptsQuery = async <
  TData = Awaited<ReturnType<typeof getPackageSuggestedPrompts>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { packageId }: GetPackageSuggestedPromptsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPackageSuggestedPrompts>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetPackageSuggestedPromptsQueryOptions({ packageId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Package Suggested Prompts
 */
export const invalidateGetPackageSuggestedPrompts = async (
  queryClient: QueryClient,
  { packageId }: GetPackageSuggestedPromptsPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetPackageSuggestedPromptsQueryKey({ packageId }) }, options);

  return queryClient;
};

/**
 * @summary Get Kb Package Validation Status
 */
export const getKbPackageValidationStatus = (
  { packageId }: GetKbPackageValidationStatusPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLKbValidationStatusResponse>(
    { url: `/kb/packages/${packageId}/validation-status`, method: 'GET', signal },
    options,
  );
};

export const getGetKbPackageValidationStatusQueryKey = ({ packageId }: GetKbPackageValidationStatusPathParameters) => {
  return ['kb', 'packages', packageId, 'validation-status'] as const;
};

export const getGetKbPackageValidationStatusQueryOptions = <
  TData = Awaited<ReturnType<typeof getKbPackageValidationStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { packageId }: GetKbPackageValidationStatusPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getKbPackageValidationStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetKbPackageValidationStatusQueryKey({ packageId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getKbPackageValidationStatus>>> = ({ signal }) =>
    getKbPackageValidationStatus({ packageId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: packageId !== null && packageId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getKbPackageValidationStatus>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetKbPackageValidationStatusQueryResult = NonNullable<
  Awaited<ReturnType<typeof getKbPackageValidationStatus>>
>;
export type GetKbPackageValidationStatusQueryError = ErrorType<TLHTTPValidationError>;

export function useGetKbPackageValidationStatus<
  TData = Awaited<ReturnType<typeof getKbPackageValidationStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetKbPackageValidationStatusPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getKbPackageValidationStatus>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getKbPackageValidationStatus>>,
          TError,
          Awaited<ReturnType<typeof getKbPackageValidationStatus>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetKbPackageValidationStatus<
  TData = Awaited<ReturnType<typeof getKbPackageValidationStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetKbPackageValidationStatusPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getKbPackageValidationStatus>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getKbPackageValidationStatus>>,
          TError,
          Awaited<ReturnType<typeof getKbPackageValidationStatus>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetKbPackageValidationStatus<
  TData = Awaited<ReturnType<typeof getKbPackageValidationStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetKbPackageValidationStatusPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getKbPackageValidationStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Kb Package Validation Status
 */

export function useGetKbPackageValidationStatus<
  TData = Awaited<ReturnType<typeof getKbPackageValidationStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { packageId }: GetKbPackageValidationStatusPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getKbPackageValidationStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetKbPackageValidationStatusQueryOptions({ packageId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Kb Package Validation Status
 */
export const prefetchGetKbPackageValidationStatusQuery = async <
  TData = Awaited<ReturnType<typeof getKbPackageValidationStatus>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { packageId }: GetKbPackageValidationStatusPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getKbPackageValidationStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetKbPackageValidationStatusQueryOptions({ packageId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Kb Package Validation Status
 */
export const invalidateGetKbPackageValidationStatus = async (
  queryClient: QueryClient,
  { packageId }: GetKbPackageValidationStatusPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetKbPackageValidationStatusQueryKey({ packageId }) }, options);

  return queryClient;
};

/**
 * @summary Get Kb Program Archive Manifest
 */
export const getKbProgramArchiveManifest = (
  { programId }: GetKbProgramArchiveManifestPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLKbArchiveManifestResponse>(
    { url: `/kb/programs/${programId}/archive-manifest`, method: 'GET', signal },
    options,
  );
};

export const getGetKbProgramArchiveManifestQueryKey = ({ programId }: GetKbProgramArchiveManifestPathParameters) => {
  return ['kb', 'programs', programId, 'archive-manifest'] as const;
};

export const getGetKbProgramArchiveManifestQueryOptions = <
  TData = Awaited<ReturnType<typeof getKbProgramArchiveManifest>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { programId }: GetKbProgramArchiveManifestPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getKbProgramArchiveManifest>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetKbProgramArchiveManifestQueryKey({ programId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getKbProgramArchiveManifest>>> = ({ signal }) =>
    getKbProgramArchiveManifest({ programId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: programId !== null && programId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getKbProgramArchiveManifest>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetKbProgramArchiveManifestQueryResult = NonNullable<
  Awaited<ReturnType<typeof getKbProgramArchiveManifest>>
>;
export type GetKbProgramArchiveManifestQueryError = ErrorType<TLHTTPValidationError>;

export function useGetKbProgramArchiveManifest<
  TData = Awaited<ReturnType<typeof getKbProgramArchiveManifest>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetKbProgramArchiveManifestPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getKbProgramArchiveManifest>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getKbProgramArchiveManifest>>,
          TError,
          Awaited<ReturnType<typeof getKbProgramArchiveManifest>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetKbProgramArchiveManifest<
  TData = Awaited<ReturnType<typeof getKbProgramArchiveManifest>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetKbProgramArchiveManifestPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getKbProgramArchiveManifest>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getKbProgramArchiveManifest>>,
          TError,
          Awaited<ReturnType<typeof getKbProgramArchiveManifest>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetKbProgramArchiveManifest<
  TData = Awaited<ReturnType<typeof getKbProgramArchiveManifest>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetKbProgramArchiveManifestPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getKbProgramArchiveManifest>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Kb Program Archive Manifest
 */

export function useGetKbProgramArchiveManifest<
  TData = Awaited<ReturnType<typeof getKbProgramArchiveManifest>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { programId }: GetKbProgramArchiveManifestPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getKbProgramArchiveManifest>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetKbProgramArchiveManifestQueryOptions({ programId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Kb Program Archive Manifest
 */
export const prefetchGetKbProgramArchiveManifestQuery = async <
  TData = Awaited<ReturnType<typeof getKbProgramArchiveManifest>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { programId }: GetKbProgramArchiveManifestPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getKbProgramArchiveManifest>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetKbProgramArchiveManifestQueryOptions({ programId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Kb Program Archive Manifest
 */
export const invalidateGetKbProgramArchiveManifest = async (
  queryClient: QueryClient,
  { programId }: GetKbProgramArchiveManifestPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetKbProgramArchiveManifestQueryKey({ programId }) }, options);

  return queryClient;
};

/**
 * @summary Trigger Schema Sync
 */
export const triggerSchemaSync = (
  tLSchemaSyncRequest: BodyType<TLSchemaSyncRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLSchemaSyncTriggerResponse>(
    {
      url: `/kb/schema-sync`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLSchemaSyncRequest,
      signal,
    },
    options,
  );
};

export const getTriggerSchemaSyncQueryKey = (tLSchemaSyncRequest?: BodyType<TLSchemaSyncRequest>) => {
  return ['POST', 'kb', 'schema-sync', tLSchemaSyncRequest] as const;
};

export const getTriggerSchemaSyncQueryOptions = <
  TData = Awaited<ReturnType<typeof triggerSchemaSync>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLSchemaSyncRequest: BodyType<TLSchemaSyncRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof triggerSchemaSync>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getTriggerSchemaSyncQueryKey(tLSchemaSyncRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof triggerSchemaSync>>> = ({ signal }) =>
    triggerSchemaSync(tLSchemaSyncRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof triggerSchemaSync>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type TriggerSchemaSyncQueryResult = NonNullable<Awaited<ReturnType<typeof triggerSchemaSync>>>;
export type TriggerSchemaSyncQueryError = ErrorType<TLHTTPValidationError>;

export function useTriggerSchemaSync<
  TData = Awaited<ReturnType<typeof triggerSchemaSync>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLSchemaSyncRequest: BodyType<TLSchemaSyncRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof triggerSchemaSync>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof triggerSchemaSync>>,
          TError,
          Awaited<ReturnType<typeof triggerSchemaSync>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useTriggerSchemaSync<
  TData = Awaited<ReturnType<typeof triggerSchemaSync>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLSchemaSyncRequest: BodyType<TLSchemaSyncRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof triggerSchemaSync>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof triggerSchemaSync>>,
          TError,
          Awaited<ReturnType<typeof triggerSchemaSync>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useTriggerSchemaSync<
  TData = Awaited<ReturnType<typeof triggerSchemaSync>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLSchemaSyncRequest: BodyType<TLSchemaSyncRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof triggerSchemaSync>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Trigger Schema Sync
 */

export function useTriggerSchemaSync<
  TData = Awaited<ReturnType<typeof triggerSchemaSync>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLSchemaSyncRequest: BodyType<TLSchemaSyncRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof triggerSchemaSync>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getTriggerSchemaSyncQueryOptions(tLSchemaSyncRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Trigger Schema Sync
 */
export const prefetchTriggerSchemaSyncQuery = async <
  TData = Awaited<ReturnType<typeof triggerSchemaSync>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLSchemaSyncRequest: BodyType<TLSchemaSyncRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof triggerSchemaSync>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getTriggerSchemaSyncQueryOptions(tLSchemaSyncRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Trigger Schema Sync
 */
export const invalidateTriggerSchemaSync = async (
  queryClient: QueryClient,
  tLSchemaSyncRequest: BodyType<TLSchemaSyncRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getTriggerSchemaSyncQueryKey(tLSchemaSyncRequest) }, options);

  return queryClient;
};

/**
 * @summary Run Schema Sync
 */
export const runSchemaSync = (
  tLSchemaSyncRequest: BodyType<TLSchemaSyncRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLSchemaSyncResultResponse>(
    {
      url: `/kb/schema-sync/run`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLSchemaSyncRequest,
      signal,
    },
    options,
  );
};

export const getRunSchemaSyncQueryKey = (tLSchemaSyncRequest?: BodyType<TLSchemaSyncRequest>) => {
  return ['POST', 'kb', 'schema-sync', 'run', tLSchemaSyncRequest] as const;
};

export const getRunSchemaSyncQueryOptions = <
  TData = Awaited<ReturnType<typeof runSchemaSync>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLSchemaSyncRequest: BodyType<TLSchemaSyncRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof runSchemaSync>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getRunSchemaSyncQueryKey(tLSchemaSyncRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof runSchemaSync>>> = ({ signal }) =>
    runSchemaSync(tLSchemaSyncRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof runSchemaSync>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type RunSchemaSyncQueryResult = NonNullable<Awaited<ReturnType<typeof runSchemaSync>>>;
export type RunSchemaSyncQueryError = ErrorType<TLHTTPValidationError>;

export function useRunSchemaSync<
  TData = Awaited<ReturnType<typeof runSchemaSync>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLSchemaSyncRequest: BodyType<TLSchemaSyncRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof runSchemaSync>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof runSchemaSync>>,
          TError,
          Awaited<ReturnType<typeof runSchemaSync>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRunSchemaSync<
  TData = Awaited<ReturnType<typeof runSchemaSync>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLSchemaSyncRequest: BodyType<TLSchemaSyncRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof runSchemaSync>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof runSchemaSync>>,
          TError,
          Awaited<ReturnType<typeof runSchemaSync>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRunSchemaSync<
  TData = Awaited<ReturnType<typeof runSchemaSync>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLSchemaSyncRequest: BodyType<TLSchemaSyncRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof runSchemaSync>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Run Schema Sync
 */

export function useRunSchemaSync<
  TData = Awaited<ReturnType<typeof runSchemaSync>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLSchemaSyncRequest: BodyType<TLSchemaSyncRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof runSchemaSync>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getRunSchemaSyncQueryOptions(tLSchemaSyncRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Run Schema Sync
 */
export const prefetchRunSchemaSyncQuery = async <
  TData = Awaited<ReturnType<typeof runSchemaSync>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLSchemaSyncRequest: BodyType<TLSchemaSyncRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof runSchemaSync>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getRunSchemaSyncQueryOptions(tLSchemaSyncRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Run Schema Sync
 */
export const invalidateRunSchemaSync = async (
  queryClient: QueryClient,
  tLSchemaSyncRequest: BodyType<TLSchemaSyncRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getRunSchemaSyncQueryKey(tLSchemaSyncRequest) }, options);

  return queryClient;
};
